import { FudoGpRepository } from './fudo-gp.repository.js';

/**
 * Repositorio de directorio Fudo (clientes/proveedores) para los pickers de
 * Empleados (PR A2b, design.md "Pickers (slice A2)"). Reusa
 * `FudoGpRepository.get` como primitiva HTTP — `/customers` y `/providers`
 * quedan gateados por `requireAdmin` desde A2a (AD14), así que TODA llamada
 * de acá manda el Bearer del admin en `Authorization`.
 *
 * Cache: module-level `Map`, TTL 5 min por recurso, con "recargar"
 * (`forceRefresh`) para saltearla explícitamente. NUNCA `localStorage`: un
 * nombre Fudo desactualizado sobreviviendo a la sesión es peor que un
 * refetch (design.md).
 */

const TTL_MS = 5 * 60 * 1000;
const PAGE_SIZE = 500;
const MAX_PAGES = 5; // backstop anti-loop, mismo criterio que fudo-gp.repository.js#getProducts

/** @type {Map<string, { data: Array<object>, expiresAt: number }>} */
const cache = new Map();

function mapCustomer(item) {
    const a = item.attributes ?? {};
    return {
        id: item.id,
        name: a.name ?? '',
        active: a.active ?? false,
        houseAccountEnabled: a.houseAccountEnabled ?? false,
        discountPercentage: a.discountPercentage ?? 0,
    };
}

/** `/providers` serializa SOLO `name` (probado en vivo 2026-08-06) — nada más que mapear. */
function mapProvider(item) {
    return { id: item.id, name: item.attributes?.name ?? '' };
}

async function fetchAllPages(endpoint, mapItem, accessToken) {
    const items = [];
    for (let page = 1; page <= MAX_PAGES; page++) {
        const res = await FudoGpRepository.get(`${endpoint}?page[size]=${PAGE_SIZE}&page[number]=${page}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        const chunk = res.data ?? [];
        items.push(...chunk.map(mapItem));
        if (chunk.length < PAGE_SIZE) break;
    }
    return items;
}

async function getResource(resource, endpoint, mapItem, { accessToken, forceRefresh = false } = {}) {
    if (!accessToken) throw new Error('El directorio de Fudo requiere una sesión de admin.');

    const cached = cache.get(resource);
    if (!forceRefresh && cached && cached.expiresAt > Date.now()) {
        return cached.data;
    }

    const data = await fetchAllPages(endpoint, mapItem, accessToken);
    cache.set(resource, { data, expiresAt: Date.now() + TTL_MS });
    return data;
}

/**
 * @param {{ accessToken: string, forceRefresh?: boolean }} params
 * @returns {Promise<Array<{ id: string, name: string, active: boolean, houseAccountEnabled: boolean, discountPercentage: number }>>}
 */
export async function getFudoCustomers({ accessToken, forceRefresh = false } = {}) {
    return getResource('customers', '/customers', mapCustomer, { accessToken, forceRefresh });
}

/**
 * @param {{ accessToken: string, forceRefresh?: boolean }} params
 * @returns {Promise<Array<{ id: string, name: string }>>}
 */
export async function getFudoProviders({ accessToken, forceRefresh = false } = {}) {
    return getResource('providers', '/providers', mapProvider, { accessToken, forceRefresh });
}

/**
 * Review follow-up P4 (2026-08-08): the cache had no invalidation export and
 * was never cleared on logout — within its 5-min TTL a response could be
 * served to a DIFFERENT admin who signs into the same tab, or keep serving
 * after Fudo access is revoked. `js/admin.js`'s `handleLogout` calls this
 * (via `admin-empleados.service.js`) so a stale directory never survives
 * past the session that fetched it.
 */
export function invalidateFudoDirectoryCache() {
    cache.clear();
}
